import React from 'react';
import { Alert } from 'react-bootstrap';
import { strings } from '../../../resources/strings';
import { ErpOrder } from '../../../models/ErpOrder';
import { ShippingLine } from '../../../models/ShippingLine';
import { Carrier } from '../../../models/Carrier';
import { CarrierStatusTracking } from '../../../models/CarrierStatusTracking';
import { Fulfillment } from '../../../models/Fulfillment';
import ServicesContext from '../../../contexts/ServicesContext';
import { GetOrderAddressesOutput } from '../../../services/Orders/GetOrderAddresses/GetOrderAddressesOutput';
import { ContainerTabContent } from '../../../common-components/TabMenu/styles';
import { WaitingIcon } from '../../../common-components/WaitingIcon/WaitingIcon';
import { CardShippingLine } from '../CardShippingLine/CardShippingLine';

interface ITabStatusCarrierProps {
  orderId?: number;
  orderNumber?: string;
  shippingLineList?: ShippingLine[];
  manuallyCollect?: (getNfe: boolean) => void;
  changeCarrier?: (shippingLineId: number) => void;
  changeTrackingCode?: (collectTrackingItemId: number, trackingCode: string) => void;
  fulfillments?: Fulfillment[];
}

const TabStatusCarrier: React.FC<ITabStatusCarrierProps> = ({
  orderId,
  orderNumber,
  shippingLineList,
  manuallyCollect,
  changeCarrier,
  changeTrackingCode,
  fulfillments
}) => {

  const [loadingErpOrderList, setLoadingErpOrderList] = React.useState<boolean>(true);
  const [loadingOrderAddresses, setLoadingOrderAddresses] = React.useState<boolean>(true);
  const [loadingCarrierList, setLoadingCarrierList] = React.useState<boolean>(true);
  const [loadingCarrierStatusTrackingList, setLoadingCarrierStatusTrackingList] = React.useState<boolean>(true);
  const [erpOrderList, setErpOrderList] = React.useState<ErpOrder[]>([]);
  const [orderAddresses, setOrderAddresses] = React.useState<GetOrderAddressesOutput|undefined|null>(null);
  const [carrierList, setCarrierList] = React.useState<Carrier[]>([]);
  const [carrierStatusTrackingList, setCarrierStatusTrackingList] = React.useState<CarrierStatusTracking[]>([]);
  
  React.useEffect(() => {
    getCarrierList();
    getStatusTrackingList();
  }, []);

  React.useEffect(() => {
    getErpOrders();
    getOrderAddresses();
  }, [orderId]);

  const {
    getErpOrdersByOrderService,
    getOrderAddressesService,
    getCarrierListService,
    getCarrierStatusTrackingListService
  } = React.useContext(ServicesContext);

  const memoShippingLines = React.useMemo(() => {
    if (loadingErpOrderList || loadingOrderAddresses || 
        loadingCarrierList || loadingCarrierStatusTrackingList) return <WaitingIcon />;

    if (!shippingLineList || shippingLineList.length === 0) {
      return (
        <Alert variant='warning' style={{ marginBottom: '0px' }}>
          {strings.NoneShippingLineFound}
        </Alert>
      );
    }

    return shippingLineList.map((shippingLine, shippingLineIndex) => {
      return (
        <CardShippingLine
          key={`shipping-line-${shippingLine.id}`}
          orderNumber={orderNumber}
          shippingLine={shippingLine}
          shippingLineNumber={shippingLineIndex+1}
          erpOrderList={erpOrderList}
          orderAddresses={orderAddresses}
          carrierList={carrierList}
          carrierStatusTrackingList={carrierStatusTrackingList}
          changeCarrier={changeCarrier}
          manuallyCollect={manuallyCollect}
          changeTrackingCode={changeTrackingCode}
          fulfillments={fulfillments}
        />
      );
    });
  }, [
    shippingLineList, erpOrderList, orderAddresses, carrierList, carrierStatusTrackingList,
    loadingErpOrderList, loadingOrderAddresses, loadingCarrierList, loadingCarrierStatusTrackingList,
    fulfillments
  ]);

  return (
    <ContainerTabContent maxHeight='60vh'>
      {memoShippingLines}
    </ContainerTabContent>
  );

  function getCarrierList() {
    setLoadingCarrierList(true);
    getCarrierListService.handle()
      .then(result => {
        setCarrierList(result?.value?.carriers || []);
      })
      .finally(() => {
        setLoadingCarrierList(false);
      });
  }

  function getStatusTrackingList() {
    setLoadingCarrierStatusTrackingList(true);
    getCarrierStatusTrackingListService.handle()
      .then(result => {
        setCarrierStatusTrackingList(result?.value?.list || []);
      })
      .finally(() => {
        setLoadingCarrierStatusTrackingList(false);
      });
  }

  function getErpOrders() {
    if (!orderId) return;

    if (!shippingLineList || shippingLineList.length === 0) {
      setLoadingErpOrderList(false);
      return;
    }

    getErpOrdersByOrderService.handle({ orderId })
      .then(result => {
        setErpOrderList(result?.value?.erpOrders || []);
      })
      .finally(() => {
        setLoadingErpOrderList(false);
      });
  }

  function getOrderAddresses() {
    if (!orderId) return;

    if (!shippingLineList || shippingLineList.length === 0) {
      setLoadingOrderAddresses(false);
      return;
    }

    getOrderAddressesService.handle({orderId})
      .then(result => {
        setOrderAddresses(result?.value);
      })
      .finally(() => {
        setLoadingOrderAddresses(false);
      });
  }
}

export { TabStatusCarrier };